import { useEffect, useState } from "react";
import { TrendingUp, DollarSign, Heart, CalendarCheck } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { api } from "../../lib/api";
import { DateFilterPicker } from "./DateFilterPicker";

const PRESETS = [
  { key: "7d", label: "7 Days" },
  { key: "30d", label: "30 Days" },
  { key: "90d", label: "90 Days" },
  { key: "all", label: "All Time" },
];

const formatTick = (date) => {
  const d = new Date(`${date}T00:00:00`);
  return isNaN(d) ? date : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const AnalyticsTab = () => {
  const [preset, setPreset] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = customStart && customEnd ? { start: customStart, end: customEnd } : { range: preset };
    api
      .get("/admin/analytics", { params })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [preset, customStart, customEnd]);

  const selectPreset = (key) => {
    setPreset(key);
    setCustomStart("");
    setCustomEnd("");
  };

  const summary = data?.summary;

  return (
    <div data-testid="admin-analytics-tab">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div className="flex flex-wrap gap-2" data-testid="analytics-preset-pills">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              data-testid={`analytics-preset-${p.key}`}
              onClick={() => selectPreset(p.key)}
              className={`text-xs font-bold uppercase tracking-wide px-4 py-2 rounded-full border-2 transition-colors ${
                preset === p.key && !customStart ? "bg-midnight-black text-clean-white border-midnight-black" : "border-soft-gray text-midnight-black/70 hover:border-midnight-black"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-end gap-3">
          <DateFilterPicker label="Custom Start" testId="analytics-start-date" value={customStart} onChange={setCustomStart} />
          <DateFilterPicker label="Custom End" testId="analytics-end-date" value={customEnd} onChange={setCustomEnd} />
        </div>
      </div>

      {loading || !data ? (
        <p className="font-medium text-midnight-black/60" data-testid="analytics-loading">Loading analytics...</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="analytics-summary-cards">
            <div className="border border-soft-gray rounded-2xl p-5" data-testid="analytics-summary-bookings">
              <CalendarCheck className="w-5 h-5 text-stranger-orange mb-3" />
              <p className="text-2xl font-extrabold">{summary.total_bookings}</p>
              <p className="text-[11px] font-bold uppercase tracking-wide text-midnight-black/60 mt-1">Total Bookings</p>
            </div>
            <div className="border border-soft-gray rounded-2xl p-5" data-testid="analytics-summary-revenue">
              <DollarSign className="w-5 h-5 text-stranger-orange mb-3" />
              <p className="text-2xl font-extrabold">${(summary.total_revenue ?? 0).toFixed(2)}</p>
              <p className="text-[11px] font-bold uppercase tracking-wide text-midnight-black/60 mt-1">Revenue Collected</p>
            </div>
            <div className="border border-soft-gray rounded-2xl p-5" data-testid="analytics-summary-match-rate">
              <Heart className="w-5 h-5 text-stranger-orange mb-3" />
              <p className="text-2xl font-extrabold">{summary.match_success_rate}%</p>
              <p className="text-[11px] font-bold uppercase tracking-wide text-midnight-black/60 mt-1">Match Success Rate</p>
            </div>
            <div className="border border-soft-gray rounded-2xl p-5" data-testid="analytics-summary-matches">
              <TrendingUp className="w-5 h-5 text-stranger-orange mb-3" />
              <p className="text-2xl font-extrabold">{summary.total_matches}</p>
              <p className="text-[11px] font-bold uppercase tracking-wide text-midnight-black/60 mt-1">Matches Created</p>
            </div>
          </div>

          <div className="border border-soft-gray rounded-2xl p-6 mb-8" data-testid="analytics-booking-trends-chart">
            <h3 className="font-extrabold uppercase text-sm mb-4">Booking Trends</h3>
            {data.booking_trends.every((d) => d.created === 0) ? (
              <p className="text-sm font-medium text-midnight-black/50">No bookings in this range.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.booking_trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#D8D6D1" />
                  <XAxis dataKey="date" tickFormatter={formatTick} tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={formatTick} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="created" name="Bookings Created" stroke="#F26522" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="matched_or_completed" name="Matched / Completed" stroke="#0A0A0A" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="border border-soft-gray rounded-2xl p-6" data-testid="analytics-revenue-chart">
              <h3 className="font-extrabold uppercase text-sm mb-4">Revenue Over Time</h3>
              {data.revenue_trend.every((d) => d.revenue === 0) ? (
                <p className="text-sm font-medium text-midnight-black/50">No verified revenue in this range.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={data.revenue_trend}>
                    <defs>
                      <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F26522" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#F26522" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#D8D6D1" />
                    <XAxis dataKey="date" tickFormatter={formatTick} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip labelFormatter={formatTick} formatter={(v) => [`$${v}`, "Revenue"]} />
                    <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#F26522" fill="url(#revenueFill)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="border border-soft-gray rounded-2xl p-6" data-testid="analytics-match-rate-chart">
              <h3 className="font-extrabold uppercase text-sm mb-4">Match Success Rate</h3>
              {data.match_rate_trend.every((d) => d.total === 0) ? (
                <p className="text-sm font-medium text-midnight-black/50">No registrations in this range.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={data.match_rate_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#D8D6D1" />
                    <XAxis dataKey="date" tickFormatter={formatTick} tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                    <Tooltip labelFormatter={formatTick} formatter={(v) => [`${v}%`, "Match Rate"]} />
                    <Line type="monotone" dataKey="rate" name="Match Rate" stroke="#0A0A0A" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
